import { Controller } from "@hotwired/stimulus"

// Shows an occasion page in an iframe inside a device — phone, tablet, laptop
// or the full width of the panel. Each device is laid out at its true size
// (the page inside is really 390/768/1280px wide) and the whole shell is
// scaled down to fit the stage, so a laptop preview inside the builder still
// shows the true desktop layout. Elements carry their per-device classes in
// `data-shell-<size>`; this swaps them in. Also toggles full screen.
//
// Outer sizes include the bezel: daisyUI's mockup-phone adds 11px a side
// (and keeps its 462:978 shape), the tablet 23px, the browser a 1px border.
const DEVICES = {
  phone: { width: 412, height: 872 },
  tablet: { width: 814, height: 1070 },
  laptop: { width: 1282, height: null } // as tall as the stage allows
}
const PADDING = 24

export default class extends Controller {
  static targets = ["stage", "box", "viewport", "display", "frame", "size", "status", "fullscreen"]
  static values = { size: { type: String, default: "full" } }

  connect() {
    this.observer = new ResizeObserver(() => this.layout())
    this.observer.observe(this.stageTarget)
    this.onFullscreenChange = () => {
      const active = document.fullscreenElement === this.stageTarget
      this.fullscreenTarget?.setAttribute("aria-pressed", String(active))
      this.layout()
    }
    document.addEventListener("fullscreenchange", this.onFullscreenChange)
    this.render()
    this.tint()
  }

  disconnect() {
    this.observer?.disconnect()
    document.removeEventListener("fullscreenchange", this.onFullscreenChange)
  }

  select({ params: { size } }) {
    this.sizeValue = size
  }

  sizeValueChanged() {
    if (this.hasStageTarget) this.render()
  }

  async toggleFullscreen() {
    try {
      if (document.fullscreenElement) await document.exitFullscreen()
      else await this.stageTarget.requestFullscreen()
    } catch (_error) {
      // Full screen can be refused (iframes, some browsers) — the preview
      // still works at its normal size.
    }
  }

  // Colour the phone's status bar and home indicator to match the page, the
  // way Safari tints its chrome. The preview is same-origin, so its
  // background is readable once it loads.
  tint() {
    try {
      const doc = this.frameTarget.contentDocument
      if (!doc?.body) return
      const background = getComputedStyle(doc.body).backgroundColor
      const [r, g, b] = background.match(/\d+(\.\d+)?/g).map(Number)
      const dark = 0.2126 * r + 0.7152 * g + 0.0722 * b < 140
      Object.assign(this.displayTarget.style, { backgroundColor: background, color: dark ? "#fff" : "#000" })
    } catch (_error) {
      // Not loaded yet, or not same-origin — the defaults are fine.
    }
  }

  render() {
    const size = this.sizeValue
    this.shells.forEach((element) => {
      Object.keys(DEVICES).concat("full").forEach((name) => {
        element.classList.remove(...this.classesFor(element, name))
      })
      element.classList.add(...this.classesFor(element, size))
    })
    this.sizeTargets.forEach((button) => {
      button.setAttribute("aria-pressed", String(button.dataset.devicePreviewSizeParam === size))
    })
    const pressed = this.sizeTargets.find((button) => button.dataset.devicePreviewSizeParam === size)
    if (this.hasStatusTarget && pressed) this.statusTarget.textContent = pressed.dataset.announce || ""
    this.layout()
  }

  get shells() {
    return this.element.querySelectorAll("[data-shell-full]")
  }

  classesFor(element, size) {
    return (element.getAttribute(`data-shell-${size}`) || "").split(/\s+/).filter(Boolean)
  }

  layout() {
    const device = DEVICES[this.sizeValue]
    const box = this.boxTarget
    const viewport = this.viewportTarget

    this.stageTarget.dataset.device = this.sizeValue
    if (!device) {
      Object.assign(box.style, { width: "100%", height: "100%" })
      Object.assign(viewport.style, { width: "100%", height: "100%", transform: "" })
      return
    }

    const available = Math.max(this.stageTarget.clientWidth - PADDING * 2, 160)
    const room = Math.max(this.stageTarget.clientHeight - PADDING * 2, 240)
    const scale = Math.min(1, available / device.width, device.height ? room / device.height : 1)
    const height = device.height || room / scale

    Object.assign(box.style, { width: `${device.width * scale}px`, height: `${height * scale}px` })
    Object.assign(viewport.style, {
      width: `${device.width}px`,
      height: `${height}px`,
      transform: scale < 1 ? `scale(${scale})` : ""
    })
  }
}
